/**
 * Series detail: season tabs and an episode list.
 *
 * Per-episode resume is read from the continue-watching feed rather than from
 * the catalog row, because progress for a series is keyed by episode and the
 * `streams` row only carries the series-level position.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ContinueWatchingItem, StreamDto } from '@sflx/shared';
import { ErrorNotice, Loading } from '../components/Feedback';
import { FavoriteButton } from '../components/FavoriteButton';
import { Icon } from '../components/Icon';
import { catalog, state, type SeriesDetailResponse, type SeriesEpisode } from '../lib/api';
import { formatClock, formatDuration, progressRatio } from '../lib/format';
import { findNextUp, type EpisodeProgress } from '../lib/next-up';
import { watchLink } from '../lib/links';
import { navigate } from '../router/router';

export function SeriesDetail({ accountId, id }: { accountId: string; id: number }) {
  const [data, setData] = useState<SeriesDetailResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [stream, setStream] = useState<StreamDto | null>(null);
  const [season, setSeason] = useState<number | null>(null);
  const [progress, setProgress] = useState<Map<string, EpisodeProgress>>(new Map());

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setSeason(null);
    catalog
      .series(accountId, id)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStream(result.stream);
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, id]);

  useEffect(() => {
    let cancelled = false;
    state
      .continueWatching(60)
      .then((result) => {
        if (cancelled) return;
        setProgress(buildProgress(result.items, accountId, id));
      })
      .catch(() => {
        // No resume marks is a cosmetic loss, not a reason to fail the page.
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, id]);

  const seasons = data?.detail.seasons ?? [];
  const activeSeason = useMemo(() => {
    if (!seasons.length) return null;
    return seasons.find((candidate) => candidate.season === season) ?? seasons[0];
  }, [seasons, season]);

  if (error) {
    return (
      <div className="page">
        <div className="wrap stack">
          <ErrorNotice error={error} />
          <button type="button" className="btn" onClick={() => navigate('/series')}>
            Back to series
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <Loading label="Loading series" />
      </div>
    );
  }

  const detail = data.detail;
  const title = detail.title ?? stream?.name ?? 'Untitled';
  const poster = detail.coverUrl ?? stream?.logoUrl ?? null;
  const year = detail.releaseDate ? detail.releaseDate.slice(0, 4) : (stream?.year ?? null);
  const rating = detail.rating ?? stream?.rating ?? null;

  const playEpisode = (episode: SeriesEpisode) => {
    navigate(
      watchLink({
        accountId,
        kind: 'series',
        xtreamId: id,
        episodeId: episode.id,
        container: episode.container,
        title: `${title} — ${episode.title}`,
      }),
    );
  };

  const nextUp = findNextUp(seasons.flatMap((entry) => entry.episodes), progress);

  return (
    <div className="page">
      <div className="wrap stack" style={{ gap: 'var(--s-6)' }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => navigate('/series')}
          style={{ alignSelf: 'flex-start' }}
        >
          <Icon name="chevronLeft" size={15} />
          Series
        </button>

        <div className="detail__hero">
          <div className="detail__poster">
            {poster ? <img src={poster} alt="" loading="lazy" decoding="async" /> : null}
          </div>

          <div className="stack">
            <div className="stack stack--tight" style={{ gap: 'var(--s-2)' }}>
              <h1>{title}</h1>
              <div className="row muted">
                {year ? <span>{year}</span> : null}
                {rating ? <span>{rating.toFixed(1)}</span> : null}
                {seasons.length ? (
                  <span>
                    {seasons.length} {seasons.length === 1 ? 'season' : 'seasons'}
                  </span>
                ) : null}
                {detail.genre ? <span>{detail.genre}</span> : null}
              </div>
            </div>

            <div className="row">
              {nextUp ? (
                <button type="button" className="btn btn--primary" onClick={() => playEpisode(nextUp.episode)}>
                  <Icon name="play" size={16} />
                  {nextUp.resume
                    ? `Resume S${pad(nextUp.episode.season)}E${pad(nextUp.episode.episodeNumber ?? 0)}`
                    : `Play S${pad(nextUp.episode.season)}E${pad(nextUp.episode.episodeNumber ?? 0)}`}
                </button>
              ) : null}
              {stream ? (
                <FavoriteButton
                  accountId={accountId}
                  kind="series"
                  xtreamId={id}
                  favorite={stream.favorite}
                  onChange={(favorite) => setStream({ ...stream, favorite })}
                  size={20}
                />
              ) : null}
            </div>

            {detail.plot ? <p className="detail__plot">{detail.plot}</p> : null}

            <dl className="kv">
              {detail.director ? (
                <>
                  <dt>Director</dt>
                  <dd>{detail.director}</dd>
                </>
              ) : null}
              {detail.cast ? (
                <>
                  <dt>Cast</dt>
                  <dd>{detail.cast}</dd>
                </>
              ) : null}
            </dl>
          </div>
        </div>

        {seasons.length ? (
          <section className="stack">
            <div className="tabs" role="tablist" aria-label="Seasons">
              {seasons.map((entry) => (
                <button
                  key={entry.season}
                  type="button"
                  role="tab"
                  className="tab"
                  aria-selected={activeSeason?.season === entry.season}
                  onClick={() => setSeason(entry.season)}
                >
                  {entry.name || `Season ${entry.season}`}
                  <span className="list-row__count"> {entry.episodes.length}</span>
                </button>
              ))}
            </div>

            <div className="stack stack--tight">
              {(activeSeason?.episodes ?? []).map((episode) => {
                const mark = progress.get(episode.id);
                const ratio = mark ? progressRatio(mark.positionS, mark.durationS) : 0;
                return (
                  <div className="episode" key={episode.id}>
                    {episode.stillUrl ? (
                      <img className="episode__still" src={episode.stillUrl} alt="" loading="lazy" decoding="async" />
                    ) : null}
                    <div className="episode__body">
                      <div className="episode__title">
                        {episode.episodeNumber ? `${episode.episodeNumber}. ` : ''}
                        {episode.title}
                      </div>
                      <div className="channel-row__sub">
                        {formatDuration(episode.durationS) ?? 'Duration unknown'}
                        {mark ? ` · watched to ${formatClock(mark.positionS)}` : ''}
                      </div>
                      {ratio > 0 ? (
                        <div className="progressbar" style={{ marginTop: 'var(--s-2)' }}>
                          <div className="progressbar__fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
                        </div>
                      ) : null}
                    </div>
                    <button type="button" className="btn btn--sm" onClick={() => playEpisode(episode)}>
                      <Icon name="play" size={15} />
                      {mark ? 'Resume' : 'Play'}
                    </button>
                  </div>
                );
              })}
              {activeSeason && !activeSeason.episodes.length ? (
                <p className="muted">The panel listed no episodes for this season.</p>
              ) : null}
            </div>
          </section>
        ) : (
          <p className="muted">The panel returned no season listing for this series.</p>
        )}
      </div>
    </div>
  );
}

function buildProgress(
  items: ContinueWatchingItem[],
  accountId: string,
  seriesId: number,
): Map<string, EpisodeProgress> {
  const map = new Map<string, EpisodeProgress>();
  for (const item of items) {
    if (item.accountId !== accountId || item.kind !== 'series' || item.id !== seriesId) continue;
    if (!item.episodeId) continue;
    map.set(item.episodeId, {
      positionS: item.positionS,
      durationS: item.durationS,
      updatedAt: item.updatedAt,
    });
  }
  return map;
}


function pad(value: number): string {
  return String(value).padStart(2, '0');
}
